import "./menu.css";
import type { HelloState } from "@tribez/shared";

export interface LobbyCallbacks {
  /** Forwards to room.send(type, payload). */
  send: (type: string, payload?: unknown) => void;
  onLeave(): void;
}

export interface LobbyApi {
  show(): void;
  hide(): void;
  refresh(state: HelloState, mySessionId: string): void;
}

const TEAM_SIZE_MIN = 1;
const TEAM_SIZE_MAX = 6;

/**
 * Pre-match lobby screen. Built ONCE in the factory (static chrome + host setup
 * inputs); `refresh` only updates text/values/visibility and fully rebuilds the
 * `.lobby-teams` subtree (which holds no focusable inputs). Host inputs are
 * never recreated and only overwritten when not focused + value differs, so the
 * UI never fights the host's typing.
 */
export function createLobbyScreen(cb: LobbyCallbacks): LobbyApi {
  const root = document.createElement("div");
  root.className = "menu-screen hidden";

  const panel = document.createElement("div");
  panel.className = "menu-panel";
  root.appendChild(panel);

  // --- Header ---
  const nameEl = document.createElement("div");
  nameEl.className = "lobby-name";
  panel.appendChild(nameEl);

  const subEl = document.createElement("div");
  subEl.className = "lobby-sub";
  panel.appendChild(subEl);

  // --- Teams (rebuilt each refresh) ---
  const teamsEl = document.createElement("div");
  teamsEl.className = "lobby-teams";
  panel.appendChild(teamsEl);

  // --- Host setup block (built ONCE; values updated in refresh) ---
  const setupEl = document.createElement("div");
  setupEl.className = "lobby-setup";

  // Size stepper row
  const sizeRow = document.createElement("div");
  sizeRow.className = "lobby-setup__row";
  const sizeLabel = document.createElement("span");
  sizeLabel.textContent = "Players per team";
  sizeRow.appendChild(sizeLabel);

  const stepper = document.createElement("div");
  stepper.className = "lobby-stepper";
  const minusBtn = document.createElement("button");
  minusBtn.type = "button";
  minusBtn.textContent = "−";
  const sizeValue = document.createElement("span");
  const plusBtn = document.createElement("button");
  plusBtn.type = "button";
  plusBtn.textContent = "+";
  stepper.appendChild(minusBtn);
  stepper.appendChild(sizeValue);
  stepper.appendChild(plusBtn);
  sizeRow.appendChild(stepper);
  setupEl.appendChild(sizeRow);

  // Allow join in progress row
  const joinRow = document.createElement("label");
  joinRow.className = "lobby-setup__row";
  const joinCheck = document.createElement("input");
  joinCheck.type = "checkbox";
  const joinText = document.createElement("span");
  joinText.textContent = "Allow join in progress";
  joinRow.appendChild(joinCheck);
  joinRow.appendChild(joinText);
  setupEl.appendChild(joinRow);

  // Private + password row
  const privRow = document.createElement("div");
  privRow.className = "lobby-setup__row";
  const privLabel = document.createElement("label");
  privLabel.className = "lobby-stepper";
  const privCheck = document.createElement("input");
  privCheck.type = "checkbox";
  const privText = document.createElement("span");
  privText.textContent = "Private";
  privLabel.appendChild(privCheck);
  privLabel.appendChild(privText);
  privRow.appendChild(privLabel);
  const pwInput = document.createElement("input");
  pwInput.type = "password";
  pwInput.className = "lobby-pw";
  pwInput.placeholder = "Password";
  privRow.appendChild(pwInput);
  setupEl.appendChild(privRow);

  panel.appendChild(setupEl);

  // --- Non-host waiting line ---
  const waitingEl = document.createElement("div");
  waitingEl.className = "lobby-waiting";
  waitingEl.textContent = "Waiting for the host to start…";
  panel.appendChild(waitingEl);

  // --- Actions ---
  const actionsEl = document.createElement("div");
  actionsEl.className = "lobby-actions";
  const startBtn = document.createElement("button");
  startBtn.type = "button";
  startBtn.className = "menu-btn";
  startBtn.textContent = "▶ Start with Bots";
  startBtn.addEventListener("click", () => cb.send("lobby:start"));
  const leaveBtn = document.createElement("button");
  leaveBtn.type = "button";
  leaveBtn.className = "menu-btn--ghost";
  leaveBtn.textContent = "Leave";
  leaveBtn.addEventListener("click", () => cb.onLeave());
  actionsEl.appendChild(startBtn);
  actionsEl.appendChild(leaveBtn);
  panel.appendChild(actionsEl);

  document.body.appendChild(root);

  // --- Host input wiring (current state held in refs so handlers read fresh values) ---
  let curTeamSize = TEAM_SIZE_MIN;

  minusBtn.addEventListener("click", () => {
    cb.send("lobby:setConfig", { teamSize: Math.max(TEAM_SIZE_MIN, curTeamSize - 1) });
  });
  plusBtn.addEventListener("click", () => {
    cb.send("lobby:setConfig", { teamSize: Math.min(TEAM_SIZE_MAX, curTeamSize + 1) });
  });
  joinCheck.addEventListener("change", () => {
    cb.send("lobby:setConfig", { allowJoinInProgress: joinCheck.checked });
  });
  const sendPrivacy = (): void => {
    cb.send("lobby:setConfig", { isPrivate: privCheck.checked, password: pwInput.value });
  };
  privCheck.addEventListener("change", sendPrivacy);
  pwInput.addEventListener("change", sendPrivacy);

  /** Overwrite a checkbox from state only when not focused + value differs. */
  function syncCheckbox(el: HTMLInputElement, value: boolean): void {
    if (document.activeElement !== el && el.checked !== value) el.checked = value;
  }

  function tagFor(
    entry: { sessionId: string; isBot: boolean },
    mySessionId: string,
    hostSessionId: string,
  ): string {
    if (entry.sessionId === mySessionId) return "(you)";
    if (entry.sessionId === hostSessionId) return "(host)";
    if (entry.isBot) return "(bot)";
    return "";
  }

  function refresh(state: HelloState, mySessionId: string): void {
    const isHost = state.hostSessionId === mySessionId;

    nameEl.textContent = state.lobbyName;
    subEl.textContent = `${state.numTeams} teams · ${state.teamSize} v ${state.teamSize}`;

    // --- Rebuild teams ---
    teamsEl.replaceChildren();
    for (let t = 0; t < state.numTeams; t++) {
      const col = document.createElement("div");
      col.className = "lobby-col";

      const hdr = document.createElement("div");
      hdr.className = "lobby-col__hdr";
      hdr.textContent = `Team ${t + 1}`;
      col.appendChild(hdr);

      const onTeam: Array<{ sessionId: string; name: string; isBot: boolean }> = [];
      state.roster.forEach((entry) => {
        if (entry.teamId === t) {
          onTeam.push({ sessionId: entry.sessionId, name: entry.name, isBot: entry.isBot });
        }
      });

      for (const entry of onTeam) {
        const seat = document.createElement("div");
        seat.className = "lobby-seat";
        if (entry.sessionId === mySessionId) seat.classList.add("lobby-seat--me");

        const nameSpan = document.createElement("span");
        nameSpan.textContent = entry.name;
        seat.appendChild(nameSpan);

        const tag = tagFor(entry, mySessionId, state.hostSessionId);
        if (tag) {
          const tagSpan = document.createElement("span");
          tagSpan.className = "lobby-seat__tag";
          tagSpan.textContent = tag;
          seat.appendChild(tagSpan);
        }

        // Host may move another (non-self, non-bot) player to the next team.
        const isHostMovable =
          isHost && entry.sessionId !== state.hostSessionId && !entry.isBot;
        if (isHostMovable) {
          seat.classList.add("lobby-seat--movable");
          const sid = entry.sessionId;
          const nextTeam = (t + 1) % state.numTeams;
          seat.addEventListener("click", () => {
            cb.send("lobby:movePlayer", { sessionId: sid, teamId: nextTeam });
          });
        }

        col.appendChild(seat);
      }

      // Pad with empty (→ move-self) seats up to teamSize.
      const empties = Math.max(0, state.teamSize - onTeam.length);
      for (let i = 0; i < empties; i++) {
        const seat = document.createElement("div");
        seat.className = "lobby-seat lobby-seat--empty";
        seat.textContent = "Empty → bot";
        seat.addEventListener("click", () => {
          cb.send("lobby:moveSelf", { teamId: t });
        });
        col.appendChild(seat);
      }

      teamsEl.appendChild(col);
    }

    // --- Host setup vs non-host waiting ---
    curTeamSize = state.teamSize;
    if (isHost) {
      setupEl.classList.remove("hidden");
      waitingEl.classList.add("hidden");
      startBtn.classList.remove("hidden");

      sizeValue.textContent = String(state.teamSize);
      minusBtn.disabled = state.teamSize <= TEAM_SIZE_MIN;
      plusBtn.disabled = state.teamSize >= TEAM_SIZE_MAX;
      syncCheckbox(joinCheck, state.allowJoinInProgress);
      syncCheckbox(privCheck, state.isPrivate);
      // Password is intentionally not synced FROM state (the server never echoes
      // it back), so the host's typed value is left untouched across refreshes.
    } else {
      setupEl.classList.add("hidden");
      waitingEl.classList.remove("hidden");
      startBtn.classList.add("hidden");
    }
  }

  return {
    show: () => root.classList.remove("hidden"),
    hide: () => root.classList.add("hidden"),
    refresh,
  };
}
